import { BlogEditorContextMenuActionEnum } from '@doltech/utils/lib/constants';
import { values } from 'lodash';
import { getRoot, types as t } from 'mobx-state-tree';

import { BlogEditorSelectStateStoreInstance } from './blog-editor-select-state.store';

export const BlogEditorContextMenuStore = t
  .model('BlogEditorContextMenuStore', {
    visible: t.optional(t.boolean, false),
    pasteGuidelineVisible: t.optional(t.boolean, false),
    hyperLinkFormVisible: t.optional(t.boolean, false),
    hoverCardFormVisible: t.optional(t.boolean, false),
    menuEnabled: t.optional(t.array(t.string), values(BlogEditorContextMenuActionEnum)),
  })
  .actions((self) => ({
    setVisible(visible) {
      self.visible = visible;
    },
    setHyperLinkFormVisible(visible) {
      self.hyperLinkFormVisible = visible;
    },
    setHoverCardFormVisible(visible) {
      self.hoverCardFormVisible = visible;
    },
    showPasteGuideline(visible) {
      self.pasteGuidelineVisible = visible;
    },
  }))
  .views((self) => ({
    getCardLogicContextMenu() {
      const rootStore: any = getRoot(self);
      if (rootStore.selectState) {
        const { isTextSelection, isInCardLogicCell, isInCardLogicHeader } =
          rootStore.selectState as BlogEditorSelectStateStoreInstance;
        return [
          isTextSelection ? BlogEditorContextMenuActionEnum.COPY : null,
          BlogEditorContextMenuActionEnum.PASTE,
          BlogEditorContextMenuActionEnum.PASTE_WITHOUT_FORMAT,
          isInCardLogicCell && !isInCardLogicHeader && isTextSelection
            ? BlogEditorContextMenuActionEnum.HYPERLINK
            : null,
        ];
      }
      return null;
    },
    getTableContextMenu() {
      const rootStore: any = getRoot(self);
      if (rootStore.selectState) {
        const { isTextSelection, isInTableHeader, isInTableCell } =
          rootStore.selectState as BlogEditorSelectStateStoreInstance;
        return [
          isTextSelection ? BlogEditorContextMenuActionEnum.COPY : null,
          BlogEditorContextMenuActionEnum.PASTE,
          BlogEditorContextMenuActionEnum.PASTE_WITHOUT_FORMAT,
          isInTableCell && !isInTableHeader && isTextSelection
            ? BlogEditorContextMenuActionEnum.HYPERLINK
            : null,
        ];
      }
      return null;
    },
  }))
  .views((self) => ({
    get menuEnabledBasedOnSelection() {
      const rootStore: any = getRoot(self);
      const selectState: BlogEditorSelectStateStoreInstance = rootStore?.selectState;
      if (selectState) {
        if (selectState.isInCardLogic && self.getCardLogicContextMenu()) {
          return self.getCardLogicContextMenu();
        }
        if (selectState.isInTable && self.getTableContextMenu()) {
          return self.getTableContextMenu();
        }
        if (selectState.isInQuoteBlock && !selectState.isInBorderShading) {
          const { isTextSelection, isInBorderShading } = selectState;
          return [
            isTextSelection ? BlogEditorContextMenuActionEnum.COPY : null,
            BlogEditorContextMenuActionEnum.PASTE,
            BlogEditorContextMenuActionEnum.PASTE_WITHOUT_FORMAT,
            isTextSelection && !isInBorderShading
              ? BlogEditorContextMenuActionEnum.BORDER_AND_SHADING
              : null,
          ];
        }
        if (selectState.isInBulletPoint) {
          const { isTextSelection, isInBorderShading } = selectState;
          return [
            isTextSelection ? BlogEditorContextMenuActionEnum.COPY : null,
            BlogEditorContextMenuActionEnum.PASTE,
            BlogEditorContextMenuActionEnum.PASTE_WITHOUT_FORMAT,
            isTextSelection ? BlogEditorContextMenuActionEnum.HYPERLINK : null,
            isTextSelection ? BlogEditorContextMenuActionEnum.INSERT_HOVER_CARD : null,
            isTextSelection && !isInBorderShading
              ? BlogEditorContextMenuActionEnum.BORDER_AND_SHADING
              : null,
          ];
        }
        if (selectState.isInBorderShading) {
          const { isTextSelection } = selectState;
          return [
            isTextSelection ? BlogEditorContextMenuActionEnum.COPY : null,
            BlogEditorContextMenuActionEnum.PASTE,
            BlogEditorContextMenuActionEnum.PASTE_WITHOUT_FORMAT,
            isTextSelection ? BlogEditorContextMenuActionEnum.HYPERLINK : null,
            isTextSelection ? BlogEditorContextMenuActionEnum.INSERT_HOVER_CARD : null,
          ];
        }
        if (selectState.isHeading2 || selectState.isHeading3) {
          const { isTextSelection } = selectState;
          return values(BlogEditorContextMenuActionEnum).filter((menuAction) => {
            return ![
              isTextSelection ? BlogEditorContextMenuActionEnum.BORDER_AND_SHADING : null,
            ].includes(menuAction as any);
          });
        }
        if (selectState.isHoverCard) {
          const { isTextSelection, isInBorderShading } = selectState;
          return [
            isTextSelection ? BlogEditorContextMenuActionEnum.COPY : null,
            BlogEditorContextMenuActionEnum.PASTE,
            BlogEditorContextMenuActionEnum.PASTE_WITHOUT_FORMAT,
            isTextSelection ? BlogEditorContextMenuActionEnum.INSERT_HOVER_CARD : null,
            isTextSelection && !isInBorderShading
              ? BlogEditorContextMenuActionEnum.BORDER_AND_SHADING
              : null,
          ];
        }
        if (selectState.isHyperLink) {
          const { isTextSelection, isInBorderShading } = selectState;
          return [
            isTextSelection ? BlogEditorContextMenuActionEnum.COPY : null,
            BlogEditorContextMenuActionEnum.PASTE,
            BlogEditorContextMenuActionEnum.PASTE_WITHOUT_FORMAT,
            isTextSelection ? BlogEditorContextMenuActionEnum.HYPERLINK : null,
            isTextSelection && !isInBorderShading
              ? BlogEditorContextMenuActionEnum.BORDER_AND_SHADING
              : null,
          ];
        }
      }

      if (!selectState.isTextSelection) {
        return [
          BlogEditorContextMenuActionEnum.PASTE,
          BlogEditorContextMenuActionEnum.PASTE_WITHOUT_FORMAT,
        ];
      }

      return values(BlogEditorContextMenuActionEnum);
    },
  }))
  .views((self) => ({
    get isInsertVocabNewsDisabled() {
      return !self.menuEnabledBasedOnSelection.includes(BlogEditorContextMenuActionEnum.VOCAB_NEWS);
    },
    get isCopyDisabled() {
      return !self.menuEnabledBasedOnSelection.includes(BlogEditorContextMenuActionEnum.COPY);
    },
    get isPasteDisabled() {
      return !self.menuEnabledBasedOnSelection.includes(BlogEditorContextMenuActionEnum.PASTE);
    },
    get isPasteWithoutFormatDisabled() {
      return !self.menuEnabledBasedOnSelection.includes(
        BlogEditorContextMenuActionEnum.PASTE_WITHOUT_FORMAT
      );
    },
    get isHyperLinkDisabled() {
      return !self.menuEnabledBasedOnSelection.includes(BlogEditorContextMenuActionEnum.HYPERLINK);
    },
    get isInsertHoverCardDisabled() {
      return !self.menuEnabledBasedOnSelection.includes(
        BlogEditorContextMenuActionEnum.INSERT_HOVER_CARD
      );
    },
    get isDatabaseObjectDisabled() {
      return !self.menuEnabledBasedOnSelection.includes(
        BlogEditorContextMenuActionEnum.DATABASE_OBJECTS
      );
    },
    get borderShadingDisabled() {
      return !self.menuEnabledBasedOnSelection.includes(
        BlogEditorContextMenuActionEnum.BORDER_AND_SHADING
      );
    },
  }));
